import styled from 'styled-components';
import React from 'react';
import useAxios from 'axios-hooks';
import {
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Autocomplete,
	Button,
	ButtonBase,
	IconButton,
	InputAdornment,
	Skeleton,
	TextField,
} from '@mui/material';
import {
	Search as SearchIcon,
	Clear as ClearIcon,
	ExpandMore as ExpandMoreIcon,
	Sort as SortIcon,
} from '@mui/icons-material';
import {
	Controller,
	useForm,
} from 'react-hook-form';
import ForumCard from './components/forum-card';
import { API_ROUTES, ROUTES } from '../../routes';
import { Auth } from '../../utils/auth';
import { getUniqueId } from '../../scripts/unique-id-manager';
import yupValidationResolver from '../../utils/yupValidationResolver';
import schema, {
	filterPrivateOptions,
	sortOptions,
} from './schema';
import { Paginator } from '../../components/paginator';
import {
	Guide,
	NGuide,
} from '../../components/guide';
import { CSSMediaSize } from '../../const';
import { JALeaderboard } from '../../components/juicyads/leaderbaord';
import { useSelector } from '../../store/store';

const guidePath: NGuide.IGuidePathItem[] = [
	{
		name: 'Home',
		route: ROUTES.home,
	},
	{
		name: 'Forum',
		route: ROUTES.forum,
	},
];

const getDefaultForm = (
	override?: Partial<NForum.IForm>,
): NForum.IForm => {
	return {
		text: '',
		sortBy: 'date_created',
		sortOrder: 'desc',
		filterPrivate: 0,
		...override,
	};
};

const perPage = 10;

const Forum = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] =
		useSearchParams();

	const { user } = useSelector((st) => st.user);

	const [
		{
			data: forumData,
			loading: forumDataLoading,
		},
		getForum,
	] = useAxios(
		{
			method: 'GET',
			url: API_ROUTES.forumPosts,
		},
		{ manual: true, autoCancel: true },
	);

	const posts = forumData?.data?.posts;
	const totalPosts = forumData?.data?.total;

	const {
		register,
		handleSubmit,
		setValue,
		formState,
		control,
	} = useForm<NForum.IForm>({
		resolver: yupValidationResolver(schema()),
		defaultValues: getDefaultForm(
			Object.fromEntries(searchParams.entries()),
		),
	});

	const onFilter = handleSubmit(
		async (values) => {
			if (values.text)
				searchParams.set('search', values.text);
			else searchParams.delete('search');
			if (
				values.sortBy &&
				values.sortBy !== 'date_created'
			)
				searchParams.set('sortBy', values.sortBy);
			else searchParams.delete('sortBy');
			if (
				values.sortOrder &&
				values.sortOrder !== 'desc'
			)
				searchParams.set(
					'sortOrder',
					values.sortOrder,
				);
			else searchParams.delete('sortOrder');
			if (
				values.filterPrivate &&
				values.filterPrivate !== 0
			)
				searchParams.set(
					'filterPrivate',
					values.filterPrivate.toString(),
				);
			else searchParams.delete('filterPrivate');
			searchParams.delete('page');
			setSearchParams(searchParams);
		},
	);

	const clearSearch = () => {
		searchParams.delete('search');
		setSearchParams(searchParams);
	};

	const onPageChange = (page: number) => {
		if (page === 1) searchParams.delete('page');
		else
			searchParams.set('page', page.toString());
		setSearchParams(searchParams);
	};

	const fetchForum = () => {
		getForum({
			headers: {
				Authorization: `Bearer ${Auth.getToken()}`,
				uniqueId: getUniqueId(),
			},
			params: {
				withAuthor: true,
				withVotes: true,
				countComments: true,
				offset:
					(Number(searchParams.get('page') || 1) -
						1) *
					perPage,
				limit: perPage,
				search: searchParams.get('search'),
				sortBy: searchParams.get('sortBy'),
				sortOrder: searchParams.get('sortOrder'),
				filterPrivate: searchParams.get(
					'filterPrivate',
				),
			},
		});
	};

	React.useEffect(() => {
		fetchForum();
	}, [searchParams]);

	return (
		<ForumStyle>
			<JALeaderboard
				id={1097812}
				idMobile={1097813}
			/>
			<Guide path={guidePath} />
			<div className="forum-header">
				<form
					className="filter-form"
					onSubmit={onFilter}
				>
					<div className="form-row-horizontal">
						<div className="filters-left">
							<TextField
								variant="outlined"
								size="small"
								placeholder="Search"
								InputProps={{
									...register('text'),
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: formState
										.dirtyFields.text ? (
										<InputAdornment position="end">
											<ButtonBase
												disableRipple
												onClick={() => {
													clearSearch();
													setValue('text', '');
												}}
											>
												<ClearIcon />
											</ButtonBase>
										</InputAdornment>
									) : null,
								}}
							/>
							<Controller
								name="sortBy"
								control={control}
								render={({ field }) => (
									<Autocomplete
										style={{ minWidth: 200 }}
										disableClearable
										onChange={(_, data) =>
											field.onChange(data?.value)
										}
										value={sortOptions.find(
											(o) =>
												o.value === field.value,
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												size="small"
												label="Sort by"
												inputProps={{
													...params.inputProps,
													readOnly: true,
												}}
											/>
										)}
										options={sortOptions}
									/>
								)}
							/>
							<Controller
								name="sortOrder"
								control={control}
								render={({ field }) => (
									<IconButton
										onClick={() =>
											field.onChange(
												field.value === 'asc'
													? 'desc'
													: 'asc',
											)
										}
									>
										<SortIcon
											style={{
												transform: `scaleY(${
													field.value === 'asc'
														? -1
														: 1
												})`,
												transition:
													'transform .2s',
											}}
										/>
									</IconButton>
								)}
							/>
							<Button type="submit">
								Search
							</Button>
						</div>
						<div className="filters-right">
							{user ? (
								<Button
									type="button"
									onClick={() =>
										navigate(ROUTES.forumPostNew)
									}
								>
									New post
								</Button>
							) : null}
						</div>
					</div>
					<div className="form-row">
						<Accordion className="accordion-simple accordion-filter">
							<AccordionSummary>
								<span>Advanced filters</span>
								<ExpandMoreIcon />
							</AccordionSummary>
							<AccordionDetails>
								<Controller
									name="filterPrivate"
									control={control}
									render={({ field }) => (
										<Autocomplete<
											(typeof filterPrivateOptions)[number],
											false,
											true,
											false
										>
											style={{ minWidth: 200 }}
											disableClearable
											onChange={(_, data) =>
												field.onChange(
													data?.value,
												)
											}
											value={filterPrivateOptions.find(
												(o) =>
													o.value === field.value,
											)}
											renderInput={(params) => (
												<TextField
													{...params}
													size="small"
													label="Private"
													inputProps={{
														...params.inputProps,
														readOnly: true,
													}}
												/>
											)}
											options={
												filterPrivateOptions
											}
										/>
									)}
								/>
							</AccordionDetails>
						</Accordion>
					</div>
				</form>
			</div>
			<div className="forum-list">
				{forumDataLoading ? (
					Array.from({ length: perPage }).map(
						(_, i) => (
							<Skeleton
								key={i}
								height={50}
								variant="rectangular"
							/>
						),
					)
				) : !posts?.length ? (
					<div className="no-posts-message">
						No posts
					</div>
				) : null}
				{posts?.map((m: any, i: number) => (
					<ForumCard
						key={i}
						forum={m}
						isAuthor={
							user &&
							m?.author &&
							user?._id === m?.author?._id
						}
						refetchPosts={fetchForum}
					/>
				))}
			</div>
			<Paginator
				props={{
					total: totalPosts,
					perPage,
				}}
				paginationProps={{
					page: Number(
						searchParams.get('page') || 1,
					),
					onChange: (_, page) =>
						onPageChange(page),
				}}
			/>
			<JALeaderboard
				id={1097814}
				idMobile={1097815}
			/>
		</ForumStyle>
	);
};

export default Forum;

export namespace NForum {
	export interface IForm {
		text?: string;
		sortBy?: string;
		sortOrder?: string;
		filterPrivate?: number;
	}
}

const ForumStyle = styled.div`
	flex-shrink: 0;
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	padding: 20px 50px;

	.no-posts-message {
		display: flex;
		justify-content: center;
	}

	.forum-header {
		margin-bottom: 16px;
		background-color: ${({ theme }) =>
			theme?.palette?.background_2?.default};
		padding: 10px;
		width: 100%;
		box-sizing: border-box;
		border-radius: 10px;
		.filter-form {
			display: flex;
			flex-direction: column;
			row-gap: 12px;
			.form-row-horizontal {
				align-items: center;
				display: flex;
				column-gap: 12px;
				> * {
					width: 50%;
				}
				.filters-left {
					display: flex;
					column-gap: 12px;
				}
				.filters-right {
					display: flex;
					justify-content: flex-end;
					column-gap: 12px;
				}
			}
			.form-row {
				//
			}
		}
	}

	.accordion-filter {
		.MuiAccordionDetails-root {
			display: flex;
		}
	}

	.forum-list {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		row-gap: 18px;
		padding: 12px;
	}

	.paginator {
		display: flex;
		justify-content: center;
	}

	${CSSMediaSize.phone_big} {
		padding: 20px 10px;
		.forum-header {
			.filter-form {
				flex-direction: column;
				row-gap: 10px;
				.filters-left {
					width: 100%;
				}
				.filters-right {
					justify-content: flex-start;
					width: 100%;
				}
			}
		}
	}
`;
